/**
 * SF-4996: companies for the Users page company filter. They come from the same sf-api list the
 * Companies page shows (`GET /admin/v1/companies`, default data provider, its `search` filter),
 * so every company can be picked, not only those the caller may hand out.
 */
import { useDataProvider, useOne } from '@refinedev/core';
import { COMPANIES_META } from '../../companies/constants';
import type { FetchCompanyPage } from '../components/InfiniteCompanySelect';

interface CompanyRecord {
  _id?: string;
  id?: string;
  name?: unknown;
}

/** Cache key for the filter's pages; the picker adds the search. */
export const COMPANY_FILTER_QUERY_KEY = ['sf-api', 'companies', 'user-filter'] as const;

const recordId = (c: CompanyRecord) => c._id ?? c.id;

/** One page of companies by name. A record without an id or a name fails the read. */
export function useFetchCompanyFilterPage(): FetchCompanyPage {
  const getDataProvider = useDataProvider();
  return async ({ search, offset, limit }) => {
    const { data } = await getDataProvider().getList<CompanyRecord>({
      resource: 'companies',
      // The companies route pages by page number; the picker only asks at whole pages.
      pagination: { current: Math.floor(offset / limit) + 1, pageSize: limit },
      filters: search ? [{ field: 'search', operator: 'contains', value: search }] : [],
      sorters: [{ field: 'name', order: 'asc' }],
      meta: COMPANIES_META,
    });
    return data.map((c) => {
      const id = recordId(c);
      if (typeof id !== 'string' || !id || typeof c.name !== 'string') {
        throw new Error('The companies answer was not in the expected shape.');
      }
      return { id, name: c.name };
    });
  };
}

/** The name of the filtered company, read on its own when the page opens from a link. */
export function useCompanyName(companyId: string | undefined) {
  const query = useOne<CompanyRecord>({
    resource: 'companies',
    id: companyId ?? '',
    meta: COMPANIES_META,
    queryOptions: {
      enabled: !!companyId,
      retry: false,
      refetchOnWindowFocus: false,
      keepPreviousData: false,
    },
    // The filter still works without the name; it shows the id instead of a toast.
    errorNotification: false,
  });
  const name = query.data?.data?.name;
  return {
    name: typeof name === 'string' ? name : undefined,
    isLoading: !!companyId && query.isLoading,
  };
}
